<template>
  <div class="admin-klarvertrag-page">
    <div class="page-header">
      <h1 class="page-title">{{ t('adminKlarvertrag.pageTitle') }}</h1>
      <p class="page-subtitle">
        Wähle einen Vertrag deiner Firma, sieh dir die Klarvertrag-Leistungen an
        und nutze direkt die Clearment KI-Vertragssscan Suche dafür.
      </p>
    </div>

    <v-alert
      v-if="errorMessage"
      type="error"
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>

    <BrainSearchAdmin
      class="brain-search-section"
      :products="brainProducts"
      :isLoadingProducts="isLoadingProducts"
      v-model:selectedProductId="selectedProductId"
    />

    <div
      v-if="selectedProduct"
      class="contract-summary"
    >
      <div class="summary-item">
        <span class="label">{{ t('adminKlarvertrag.fields.product') }}</span>
        <span class="value">{{ selectedProduct.prod_name || 'Unbekannt' }}</span>
      </div>
      <div class="summary-item">
        <span class="label">{{ t('adminKlarvertrag.fields.category') }}</span>
        <span class="value">
          {{ formatProductCategory(selectedProduct.prod_category) }}
        </span>
      </div>
      <div class="summary-item">
        <span class="label">{{ t('adminKlarvertrag.fields.klarvertragId') }}</span>
        <span class="value">{{ selectedProduct.prod_klv_id || '—' }}</span>
      </div>
      <div class="summary-item">
        <span class="label">{{ t('adminKlarvertrag.fields.klarvertragType') }}</span>
        <span class="value">
          {{ selectedProduct.prod_klv_type || '—' }}
        </span>
      </div>
    </div>

    <div v-else-if="!isLoadingProducts" class="info-state">
      <v-icon size="48" color="#EA5545" class="mb-2">mdi-package-variant</v-icon>
      <p class="info-text">Wähle einen Klarvertrag, um Details zu laden.</p>
    </div>

    <div v-if="isLoadingKlarvertrag" class="state-block" data-state="loading">
      <v-progress-circular indeterminate color="#EA5545" size="64"></v-progress-circular>
      <p class="state-text">Klarvertrag wird geladen…</p>
    </div>

    <div
      v-else-if="selectedProduct && klarvertrag.length > 0"
      class="questions-container"
    >
      <div class="questions-header">
        <h2>Leistungsübersicht</h2>
        <v-btn
          color="#EA5545"
          variant="tonal"
          prepend-icon="mdi-file-pdf-box"
          @click="generatePdf"
        >
          Als PDF exportieren
        </v-btn>
      </div>

      <v-card
        v-for="frage in klarvertrag"
        :key="frage.frage_id"
        class="question-card"
        elevation="1"
      >
        <v-card-text>
          <div class="question-header">
            <v-chip size="small" color="#EA5545" class="cluster-chip">
              Cluster {{ frage.frage_cluster }} · {{ frage.frage_sort }}
            </v-chip>
          </div>
          <h3 class="question-text">{{ frage.frage_text }}</h3>

          <div
            v-if="frage.options && frage.options.length > 0"
            class="options-container"
          >
            <div
              v-for="antwort in frage.options"
              :key="antwort.antwort_id"
              class="answer-item"
            >
              <div v-if="frage.frage_type == 1" class="answer-content">
                <v-icon size="16" color="#4caf50" class="mr-2">mdi-text</v-icon>
                <span v-html="nl2br(antwort.antwort_value)"></span>
              </div>

              <div v-else class="answer-content">
                <v-icon size="16" color="#2196f3" class="mr-2">mdi-checkbox-marked-circle</v-icon>
                <div class="option-details">
                  <span class="option-text">{{ antwort.option_text }}</span>
                  <div v-if="antwort.option_add_value == 1" class="option-value">
                    <strong v-html="nl2br(antwort.antwort_value)"></strong>
                    <span v-if="antwort.option_add_value_type == 1" class="value-unit"> Jahre</span>
                    <span v-if="antwort.option_add_value_type == 2" class="value-unit"> Zahn/Zähne</span>
                    <span v-if="antwort.option_add_value_type == 4" class="value-unit"> %</span>
                    <span v-if="antwort.option_add_value_type == 3" class="value-unit"> €</span>
                    <span v-if="antwort.option_add_value_type == 5" class="value-unit"> Monate</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="no-options">
            <v-icon size="20" color="#999">mdi-information-outline</v-icon>
            <span>Keine Optionen für diese Frage vorhanden.</span>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <div
      v-else-if="selectedProduct && !klarvertrag.length && selectedProduct.prod_klv_id"
      class="state-block" data-state="empty"
    >
      <v-icon size="80" color="#ccc">mdi-file-question</v-icon>
      <h3 class="state-title">Keine Klarvertrag-Daten</h3>
      <p class="state-text">
        Für diesen Vertrag konnten keine Klarvertrag-Leistungen gefunden werden.
      </p>
    </div>

    <div
      v-else-if="selectedProduct && !selectedProduct.prod_klv_id"
      class="info-state"
    >
      <v-icon size="48" color="#ffb300" class="mb-2">mdi-alert-circle-outline</v-icon>
      <p class="info-text">Für dieses Produkt ist keine Klarvertrag-ID hinterlegt.</p>
    </div>

    <div v-else class="state-block" data-state="empty">
      <v-icon size="80" color="#EA5545">mdi-package-variant</v-icon>
      <h3 class="state-title">Vertrag auswählen</h3>
      <p class="state-text">
        Wähle einen Vertrag, um dessen Klarvertrag-Leistungen einzusehen.
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted, watch } from 'vue'
import BrainSearchAdmin from '@/components/objects/BrainSearchAdmin.vue'
import { CLEARMENT_TITLE, CLEARMENT_DESCRIPTION } from '@/constants/documentHead'
import { useLanguage } from '@/composables/useLanguage'

const baseCalls = inject('baseCalls')
const updateDocumentHead = inject('updateDocumentHead')
const { t } = useLanguage()
updateDocumentHead?.(CLEARMENT_TITLE, CLEARMENT_DESCRIPTION)

const brainProducts = ref([])
const isLoadingProducts = ref(false)
const selectedProductId = ref(null)
const klarvertrag = ref([])
const isLoadingKlarvertrag = ref(false)
const errorMessage = ref('')

const selectedProduct = computed(() => {
  if (!selectedProductId.value && selectedProductId.value !== 0) {
    return null
  }
  const numericId = Number(selectedProductId.value)
  if (Number.isNaN(numericId)) {
    return null
  }
  return brainProducts.value.find(product => Number(product.prod_id) === numericId) || null
})

const selectedProductSupportsBrainSearch = computed(() => {
  return Number(selectedProduct.value?.prod_brain_search || 0) === 1
})

async function loadBrainProducts() {
  isLoadingProducts.value = true
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getbrainproducts'
    })
    if (response.data.success && Array.isArray(response.data.products)) {
      brainProducts.value = response.data.products
    } else {
      brainProducts.value = []
      errorMessage.value = response.data.message || ''
    }
    if (
      selectedProductId.value &&
      !brainProducts.value.some(product => Number(product.prod_id) === Number(selectedProductId.value))
    ) {
      selectedProductId.value = null
    }
  } catch (err) {
    console.error('Error loading brain products:', err)
    brainProducts.value = []
    errorMessage.value = 'Produkte konnten nicht geladen werden.'
  } finally {
    isLoadingProducts.value = false
  }
}

async function loadKlarvertrag() {
  klarvertrag.value = []
  errorMessage.value = ''

  if (!selectedProduct.value) {
    return
  }

  if (!selectedProduct.value.prod_klv_id) {
    errorMessage.value = 'Für dieses Produkt ist keine Klarvertrag-ID hinterlegt.'
    return
  }

  isLoadingKlarvertrag.value = true
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getklarvertrag',
      tarif_id: selectedProduct.value.prod_klv_id,
      prod_id: selectedProduct.value.prod_id,
      limit: 200
    })

    if (response.data.success && response.data.klarvertrag) {
      klarvertrag.value = response.data.klarvertrag
    } else {
      klarvertrag.value = []
      errorMessage.value = response.data.message || 'Keine Klarvertrag-Daten gefunden.'
    }
  } catch (err) {
    console.error('Error loading klarvertrag:', err)
    klarvertrag.value = []
    errorMessage.value = 'Klarvertrag konnte nicht geladen werden.'
  } finally {
    isLoadingKlarvertrag.value = false
  }
}

function generatePdf() {
  if (!selectedProduct.value || !selectedProduct.value.prod_klv_id) return
  const url = `/clearmentget/generateklarvertragpdf/${selectedProduct.value.prod_klv_id}`
  window.open(url, '_blank')
}

function nl2br(text) {
  if (!text) return ''
  return text.replace(/\n/g, '<br>')
}

function formatProductCategory(category) {
  const num = Number(category)
  if (Number.isNaN(num)) {
    return category || '—'
  }
  return num
}

onMounted(() => {
  loadBrainProducts()
})

watch(selectedProductId, (newValue) => {
  if (newValue && selectedProductSupportsBrainSearch.value) {
    loadKlarvertrag()
  } else {
    klarvertrag.value = []
    errorMessage.value = ''
  }
})
</script>

<style scoped src="@/assets/styles/sfc/components/sites/AdminKlarvertragIndex.css"></style>
